// Create Schedule (/daily-schedule/create, view, edit): the child-wise plan's One-on-One columns,
// what they offer and what Playground proposes for each child.
//
// Options are the admin's published course content (GET /api/lesson-plans/catalog): scene courses by
// Scenes row (else Difficulty Level), story and poem courses, every Life Skills course, and the
// courses placed in English > Scenes / Action Songs or a Stories row of the admin Courses page. Each
// item carries its Syllabus Tracker column (topic + session number), which the children's practice
// uploads and scores are filed under.
// Proposals come from each child's records (GET /api/lesson-plans/children-progress): the latest
// Syllabus Tracker score per topic, practice uploads and the latest GCompris game, named by the course
// item of that column. An Action Songs session no course item has, and a Computer apps session, is
// named as the tracker names that column (the response's `columnNames`, else "Action Song 16"); an
// Action Songs "Next" column that neither has isn't proposed.
//
// A saved plan row still has one `oneOnOne` text (the coach view, share text and exports read it):
// one chosen item is saved as it is, several as "Beginner Scenes: Scene 1 · Next | Stories: Lost Calf".
import { ACTION_SONGS, BALGOPAL_STORIES, BALGRUHA_STORIES, SAINT_STORIES, SCENES } from "../data/learningZoneMockData";
import { COMPUTER_APPS, TBD, categoryOf, rowCategory } from "../constants/lessonPlanCatalog";

// The Learning Zone's sample scenes up to this number were Beginner Scenes, later ones Intermediate
// Scenes (older plans are read back with it).
export const BEGINNER_SCENES_UP_TO = 2;
// Syllabus Tracker score bands (utils/syllabusTopics.js): 26+ completed, 15-25 in progress, below 15 started.
const COMPLETED_FROM = 26;
const PRACTICE_FROM = 15;
const DEFAULT_MAX_SCORE = 30;

export const LEARNING_COLUMNS = [
  { key: "beginnerScene", label: "Beginner Scenes" },
  { key: "intermediateScene", label: "Intermediate Scenes" },
  { key: "actionSong", label: "Action Songs" },
  { key: "story", label: "Stories" },
  { key: "lifeSkill", label: "Life Skills" },
];
export const emptyLearning = () => Object.fromEntries(LEARNING_COLUMNS.map((c) => [c.key, ""]));

const SEPARATOR = " | ";
const KINDS = ["Next", "Practice", "Revision"];
const withKinds = (name) => KINDS.map((k) => `${name} · ${k}`);
// "|" separates the parts of a saved value, so it can't appear inside one.
const clean = (value) => String(value || "").replace(/\|/g, "/").trim();

// A Syllabus Tracker column as the coach grid heads it: its name (children-progress `columnNames`:
// the chapter's title, else the coach's), else the topic's word and number.
const TRACKER_WORD = { "Action Songs": "Action Song", "Computer apps": "Computer App" };
const trackerName = (topic, n, names) => clean(names?.[topic]?.[n]) || `${TRACKER_WORD[topic]} ${n}`;

// Session numbers a topic's tracker columns can be proposed at for these children (each one's last
// scored session and the one after, last upload), plus every column the tracker names. Without
// `unnamedNext` the one after is left out unless the tracker names it (it doesn't exist yet).
const trackerNumbers = (topic, children, names, { unnamedNext = true } = {}) => {
  const numbers = new Set(Object.keys(names?.[topic] || {}).map(Number));
  (children || []).forEach((c) => {
    const p = c?.syllabus?.[topic];
    if (p?.lastSession > 0) [p.lastSession, ...(unnamedNext ? [p.lastSession + 1] : [])].forEach((n) => numbers.add(n));
    if (p?.lastSubmittedSession > 0) numbers.add(p.lastSubmittedSession);
  });
  return [...numbers].filter((n) => Number.isInteger(n) && n > 0).sort((a, b) => a - b);
};

// The catalog's courses by column: { [column key]: [{ label: course, items: [{ name, topic,
// sessionNumber }] }] }. Each course numbers its own items ("Scene 1" in every course), so an item
// whose title another course also has names its row (else its course), as the coach tracker does:
// "Scene 1 · Beginners".
const CATALOG_COLUMNS = [
  ["beginnerScene", (catalog) => (catalog.scenes || []).filter((c) => c.level === "Beginner")],
  ["intermediateScene", (catalog) => (catalog.scenes || []).filter((c) => c.level === "Intermediate")],
  ["actionSong", (catalog) => catalog.actionSongs || []],
  ["story", (catalog) => catalog.stories || []],
  ["lifeSkill", (catalog) => catalog.lifeSkills || []],
];
export const courseItems = (catalog) => {
  const columns = CATALOG_COLUMNS.map(([key, pick]) => [key, pick(catalog || {})]);
  const coursesWith = new Map();
  columns.forEach(([, courses]) => courses.forEach((c) => new Set((c.items || []).map(clean).filter(Boolean))
    .forEach((title) => coursesWith.set(title, (coursesWith.get(title) || 0) + 1))));
  const used = new Set();
  const named = (c) => (c.items || [])
    .map((title, i) => ({ title: clean(title), session: c.sessions?.[i] || null }))
    .filter((item) => item.title)
    .map(({ title, session }) => {
      const places = [...new Set([clean(c.row), clean(c.course)].filter(Boolean))];
      const name = (coursesWith.get(title) > 1 ? places.map((place) => `${title} · ${place}`) : [title]).find((n) => !used.has(n));
      if (!name) return null;
      used.add(name);
      return { name, topic: session?.topic || "", sessionNumber: session?.sessionNumber || null };
    })
    .filter(Boolean);
  return Object.fromEntries(columns.map(([key, courses]) => [key, courses.map((c) => ({ label: c.course, items: named(c) }))]));
};

// The course item of a tracker column, among the given columns' courses: { key, name } or null.
const itemAt = (items, keys, topic, n) => {
  for (const key of keys) {
    const item = items[key].flatMap((c) => c.items).find((i) => i.topic === topic && i.sessionNumber === n);
    if (item) return { key, name: item.name };
  }
  return null;
};
const SCENE_KEYS = ["beginnerScene", "intermediateScene"];

// Every column's dropdown: [{ label, options: [value] }], values unique within a column. `names` =
// children-progress `columnNames`. The courses' items are one list without a heading: course names
// aren't shown (an item another course also has already names its row, "Scene 1 · Beginners").
export const buildColumnOptions = (catalog = {}, children = [], names = {}) => {
  const items = courseItems(catalog);
  const courseGroups = (key, format = (name) => [name]) =>
    [{ label: "", options: items[key].flatMap((c) => c.items.flatMap((i) => format(i.name))) }];

  const columns = {
    beginnerScene: courseGroups("beginnerScene", withKinds),
    intermediateScene: courseGroups("intermediateScene", withKinds),
    actionSong: [
      ...courseGroups("actionSong"),
      {
        label: "Syllabus Tracker",
        options: trackerNumbers("Action Songs", children, names, { unnamedNext: false })
          .filter((n) => !itemAt(items, ["actionSong"], "Action Songs", n))
          .map((n) => trackerName("Action Songs", n, names)),
      },
    ],
    story: courseGroups("story"),
    lifeSkill: courseGroups("lifeSkill"),
  };
  // Drop repeats and empty groups.
  return Object.fromEntries(Object.entries(columns).map(([key, groups]) => {
    const seen = new Set();
    const unique = groups
      .map((g) => ({ ...g, options: g.options.filter((o) => !seen.has(o) && seen.add(o)) }))
      .filter((g) => g.options.length);
    return [key, unique];
  }));
};

// ------------------------------------------------------------------ proposals

// What a child should do next in a topic whose items are numbered like its Syllabus Tracker sessions.
// Returns { n, kind } or null when nothing is recorded.
const nextStep = (progress, maxScore) => {
  if (!progress) return null;
  const scored = progress.lastSession || 0;
  const uploaded = progress.lastSubmittedSession || 0;
  // A practice uploaded for a later item than the last scored one hasn't been scored yet.
  if (uploaded > scored) return { n: uploaded, kind: "Practice" };
  if (!scored) return null;
  const score = progress.lastScore;
  if (score === null || score === undefined) return { n: scored, kind: "Practice" };
  if (score >= COMPLETED_FROM * (maxScore / DEFAULT_MAX_SCORE)) return { n: scored + 1, kind: "Next" };
  if (score >= PRACTICE_FROM * (maxScore / DEFAULT_MAX_SCORE)) return { n: scored, kind: "Practice" };
  return { n: scored, kind: "Revision" };
};

// A Syllabus Tracker Computer apps activity in the Computer Apps column: one of the plan's apps when
// it is one ("Gcomprise" -> GCompris), else "<activity> · Next|Practice|Revision".
const TRACKER_APPS = [
  { match: /g\s*compris/i, app: (kind) => (kind === "Next" ? "GCompris (Next)" : "GCompris (Practice)") },
  { match: /typing/i, app: () => "Typing Master" },
  { match: /art\s*weaver/i, app: () => "Art Weaver" },
  { match: /\bcanva\b/i, app: () => "Canva (Drawing)" },
];
const computerActivity = (name, kind) => TRACKER_APPS.find((a) => a.match.test(name))?.app(kind) || `${name} · ${kind}`;

// GCompris records every game; without one, the Syllabus Tracker's Computer apps grading; the other
// apps rotate so the machines are shared out.
const ROTATION = ["GCompris (Next)", "Typing Master", "Art Weaver", "Canva (Drawing)"];
export const proposeComputer = (child, index, maxScore = DEFAULT_MAX_SCORE, names = {}) => {
  if (child?.gcompris) return child.gcompris.passed || child.gcompris.completed ? "GCompris (Next)" : "GCompris (Practice)";
  const graded = nextStep(child?.syllabus?.["Computer apps"], maxScore);
  if (graded) return computerActivity(trackerName("Computer apps", graded.n, names), graded.kind);
  return ROTATION[index % ROTATION.length];
};

// Computer Apps options besides the apps: the tracker's Computer apps activities these children can
// be proposed (proposeComputer), each as Next / Practice / Revision.
export const trackerComputerGroups = (children = [], names = {}) => {
  const options = [...new Set(trackerNumbers("Computer apps", children, names)
    .flatMap((n) => KINDS.map((kind) => computerActivity(trackerName("Computer apps", n, names), kind))))]
    .filter((value) => !COMPUTER_APP_NAMES.includes(value));
  return options.length ? [{ label: "Syllabus Tracker", options }] : [];
};

// A proposed row: { computer, learning, note }. `child` is a children-progress entry (or undefined
// when the child has no records yet); `names` its response's `columnNames`; `catalog` the course
// content (GET /api/lesson-plans/catalog), which names what the records point to. Special
// instructions are the admin's to write, so the note starts empty.
export const proposeRow = (child, index, maxScore = DEFAULT_MAX_SCORE, names = {}, catalog = {}) => {
  const learning = emptyLearning();
  const items = courseItems(catalog);

  // Scenes: the course scene of the child's next step, in its course's level column; with no Scenes
  // record, the first Beginner scene.
  const scene = nextStep(child?.syllabus?.Scenes, maxScore);
  const sceneItem = scene && itemAt(items, SCENE_KEYS, "Scenes", scene.n);
  const firstScene = items.beginnerScene.flatMap((c) => c.items)[0];
  if (sceneItem) learning[sceneItem.key] = `${sceneItem.name} · ${scene.kind}`;
  else if (!scene && firstScene) learning.beginnerScene = `${firstScene.name} · Next`;

  // Action Songs: the course song of the next step, else the tracker's column (a next one only when
  // the tracker has it).
  const song = nextStep(child?.syllabus?.["Action Songs"], maxScore);
  const songItem = song && itemAt(items, ["actionSong"], "Action Songs", song.n);
  if (songItem) learning.actionSong = songItem.name;
  else if (song && (song.kind !== "Next" || names?.["Action Songs"]?.[song.n])) learning.actionSong = trackerName("Action Songs", song.n, names);

  // Stories: the story topic scored most recently, named by its course item.
  const storyTopics = [...new Set(items.story.flatMap((c) => c.items.map((i) => i.topic)).filter(Boolean))];
  const story = storyTopics
    .map((topic) => ({ topic, progress: child?.syllabus?.[topic] }))
    .filter((t) => t.progress)
    .sort((a, b) => new Date(b.progress.scoredAt || 0) - new Date(a.progress.scoredAt || 0))
    .map(({ topic, progress }) => {
      const step = nextStep(progress, maxScore);
      return step && itemAt(items, ["story"], topic, step.n);
    })
    .find(Boolean);
  if (story) learning.story = story.name;

  return { computer: proposeComputer(child, index, maxScore, names), learning, note: "" };
};

// ------------------------------------------------------- saved text <-> columns

const LABEL_TO_KEY = Object.fromEntries(LEARNING_COLUMNS.map((c) => [c.label, c.key]));
const CATEGORY_TO_KEY = {
  ...LABEL_TO_KEY,
  Beginners: "beginnerScene",
  Intermediate: "intermediateScene",
  "Vocal Songs": "actionSong",
  "Instrumental Songs": "actionSong",
};
const sceneColumn = (n) => (n <= BEGINNER_SCENES_UP_TO ? "beginnerScene" : "intermediateScene");
const sceneByNumber = (value) => {
  const n = Number(String(value).match(/scene\s*(\d+)/i)?.[1]);
  return n ? sceneColumn(n) : "intermediateScene";
};

// The Learning Zone's sample items, which plans saved before 2026-10-28 offered.
const songLabel = (song) => `${song.type === "vocal" ? "Vocal" : "Instrumental"} · ${song.title}`;
const SAMPLE_OPTIONS = {
  beginnerScene: [{ options: SCENES.filter((s) => s.id <= BEGINNER_SCENES_UP_TO).flatMap((s) => withKinds(s.title)) }],
  intermediateScene: [{ options: SCENES.filter((s) => s.id > BEGINNER_SCENES_UP_TO).flatMap((s) => withKinds(s.title)) }],
  actionSong: [{ options: ACTION_SONGS.map(songLabel) }],
  story: [{ options: [...BALGOPAL_STORIES, ...SAINT_STORIES, ...BALGRUHA_STORIES].map((s) => clean(s.title)) }],
};

// The column an older one-item plan value belongs in: the column offering it, the saved column, then
// the sample item's.
const columnOf = (value, category, options) => {
  const listedIn = (groups) => Object.keys(groups || {}).find((key) => groups[key].some((g) => g.options.includes(value)));
  const listed = listedIn(options);
  if (listed) return listed;
  const byCategory = (cat) => (cat === "Scenes" ? sceneByNumber(value) : CATEGORY_TO_KEY[cat]);
  const known = byCategory(categoryOf(value)) || byCategory(category);
  if (known) return known;
  const sample = listedIn(SAMPLE_OPTIONS);
  if (sample) return sample;
  if (/scene/i.test(value)) return sceneByNumber(value);
  if (/song|poem|rhyme|vocal|instrumental/i.test(value)) return "actionSong";
  if (/stor(y|ies)/i.test(value)) return "story";
  return "lifeSkill";
};

// Columns of a saved plan row ({ oneOnOne, category }).
export const learningFromSaved = (row, options) => {
  const learning = emptyLearning();
  const text = String(row?.oneOnOne || "").trim();
  if (!text || text === TBD) return learning;
  const parts = text.split(SEPARATOR).map((part) => part.match(/^(.+?):\s(.+)$/));
  if (parts.every((m) => m && LABEL_TO_KEY[m[1]])) {
    parts.forEach(([, label, value]) => { learning[LABEL_TO_KEY[label]] = value.trim(); });
    return learning;
  }
  learning[columnOf(text, row?.category, options)] = text;
  return learning;
};

const sameLearning = (a, b) => LEARNING_COLUMNS.every((c) => (a?.[c.key] || "") === (b?.[c.key] || ""));

// The saved { oneOnOne, category } for a row's columns. A row loaded from a saved plan and left as it
// was keeps its saved text, so editing other rows doesn't rewrite it.
export const savedLearning = (learning, base) => {
  if (base && sameLearning(learning, base.learning)) {
    const oneOnOne = base.oneOnOne || TBD;
    return { oneOnOne, category: rowCategory({ oneOnOne, category: base.category }) };
  }
  const used = LEARNING_COLUMNS.filter((c) => clean(learning?.[c.key]));
  if (!used.length) return { oneOnOne: TBD, category: categoryOf(TBD) };
  const first = clean(learning[used[0].key]);
  const fromCatalogue = categoryOf(first);
  const category = fromCatalogue === "Other" ? used[0].label : fromCatalogue;
  if (used.length === 1) return { oneOnOne: first, category };
  return { oneOnOne: used.map((c) => `${c.label}: ${clean(learning[c.key])}`).join(SEPARATOR), category };
};

// Column labels a plan uses, e.g. "Intermediate Scenes, Stories" (its learning focus).
export const usedColumns = (rows) => LEARNING_COLUMNS
  .filter((c) => rows.some((r) => clean(r.learning?.[c.key])))
  .map((c) => c.label)
  .join(", ");

export const COMPUTER_APP_NAMES = COMPUTER_APPS.map((a) => a.name);
